/**
 * Co-located subcomponents for the owner-view Conversations tab.
 *
 * - ConversationsChips: All / Phone / Website chat channel filter chips (with
 *   counts), client-side filtering only.
 * - ConversationsRow: one calm read-only row — initial avatar + name + one-line
 *   gist, then a tiny outcome dot + label + relative time. No reply controls.
 * - ConversationsEmpty: purpose-built empty state ("No conversations yet" for
 *   the whole genie, or "Nothing on this channel yet" for a filtered view).
 *
 * Brand law: genie tokens only; wireframe outcome tints mapped to brand dots
 * (resolved -> genie-success, handed off -> genie-secondary, lead captured ->
 * genie-accent, message taken -> gray). Read-only viewer; "Tell our genie" is
 * the only mutating door and it lives in the shell, not here.
 */
import { GenieDisc } from "@/components/voice-genie/atoms";
import type { Conversation, ConversationOutcome } from "@/types/voice-vision";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const PINK = "#FF4DB8";

/* ───────────────────────────── channel chips ───────────────────────────── */

export type ConversationsFilterKey = "all" | "phone" | "web";

interface ConversationsChipsProps {
  active: ConversationsFilterKey;
  counts: { all: number; phone: number; web: number };
  onChange: (key: ConversationsFilterKey) => void;
}

export const ConversationsChips = ({ active, counts, onChange }: ConversationsChipsProps) => {
  const chips: { key: ConversationsFilterKey; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "phone", label: "Phone", count: counts.phone },
    { key: "web", label: "Website chat", count: counts.web },
  ];

  return (
    <div className="flex flex-row flex-wrap gap-2 mb-3.5">
      {chips.map((chip) => {
        const on = active === chip.key;
        return (
          <button
            key={chip.key}
            type="button"
            onClick={() => onChange(chip.key)}
            aria-pressed={on}
            className={`h-[34px] pl-3.5 pr-3 rounded-full flex items-center gap-1.5 transition-all ${
              on
                ? "bg-white border border-genie-primary shadow-sm"
                : "bg-transparent border border-genie-border hover:border-genie-primary/40"
            }`}
          >
            <span
              className={`text-[13px] ${
                on ? "font-bold text-[#222B45]" : "font-semibold text-gray-500"
              }`}
            >
              {chip.label}
            </span>
            <span
              className={`inline-flex items-center justify-center h-[18px] min-w-[18px] px-1.5 rounded-full text-[10.5px] font-bold ${
                on ? "bg-genie-primary/10 text-genie-primary" : "bg-gray-100 text-gray-500"
              }`}
            >
              {chip.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/* ──────────────────────────── outcome mapping ───────────────────────────── */

interface OutcomeMeta {
  label: string;
  /** Tailwind bg class for the tiny status dot (brand token only). */
  dot: string;
}

const OUTCOME_META: Record<ConversationOutcome, OutcomeMeta> = {
  resolved: { label: "Resolved", dot: "bg-genie-success" },
  "handed-off": { label: "Handed to you", dot: "bg-genie-secondary" },
  "lead-captured": { label: "Lead captured", dot: "bg-genie-accent" },
  missed: { label: "Message taken", dot: "bg-gray-400" },
};

/* Relative "how long ago" label, kept calm and plain-language. */
const relativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMin = Math.max(0, Math.round((Date.now() - then) / 60_000));
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  return `${diffDay}d ago`;
};

/* ──────────────────────────── conversation row ──────────────────────────── */

interface ConversationsRowProps {
  conversation: Conversation;
  first: boolean;
}

export const ConversationsRow = ({ conversation, first }: ConversationsRowProps) => {
  const outcome = OUTCOME_META[conversation.outcome];
  const initial = conversation.caller.trim().charAt(0).toUpperCase() || "?";

  return (
    <div
      className={`flex items-center gap-3.5 py-3.5 ${first ? "" : "border-t border-[#F3F4F6]"}`}
    >
      {/* initial avatar — gives the list a face */}
      <div className="w-9 h-9 rounded-full bg-[#F1F5F9] flex items-center justify-center flex-shrink-0">
        <span className="text-[13px] font-bold text-[#64748B]">{initial}</span>
      </div>

      {/* name + one-line gist */}
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-2.5">
        <span className="text-sm font-bold text-[#222B45] flex-shrink-0">
          {conversation.caller}
        </span>
        <span className="text-[13px] text-[#64748B] truncate min-w-0">
          {conversation.summary}
        </span>
      </div>

      {/* tiny outcome dot + label + relative time */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`w-[7px] h-[7px] rounded-full ${outcome.dot}`} />
        <span className="text-xs font-semibold text-[#64748B] whitespace-nowrap hidden sm:inline">
          {outcome.label}
        </span>
        <span className="text-xs text-gray-500 whitespace-nowrap">
          · {relativeTime(conversation.startedAt)}
        </span>
      </div>
    </div>
  );
};

/* ──────────────────────────── empty states ──────────────────────────────── */

interface ConversationsEmptyProps {
  /** "genie" = no conversations at all yet; "filter" = none on the active chip. */
  scope: "genie" | "filter";
}

export const ConversationsEmpty = ({ scope }: ConversationsEmptyProps) => {
  if (scope === "filter") {
    return (
      <div className="flex flex-col items-center text-center py-10 px-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
          style={{ backgroundColor: `${PINK}1a` }}
        >
          <GenieIcon name="chat" size={24} className="w-6 h-6" />
        </div>
        <p className="text-sm font-bold text-[#222B45]">Nothing on this channel yet</p>
        <p className="text-[13px] text-gray-500 mt-1 max-w-[340px]">
          Try a different channel above to see your genie's recent conversations.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center py-16 px-4">
      <div className="mb-4">
        <GenieDisc size={88} />
      </div>
      <h2 className="text-lg font-bold text-[#222B45]">No conversations yet</h2>
      <p className="text-sm text-gray-500 mt-1.5 max-w-[380px] leading-[1.55]">
        As soon as your genie answers a call or a website chat, the gist of every
        conversation will appear here.
      </p>
    </div>
  );
};
